// Relação com cada jogador (o estado do botão de amizade), guardada nesta aba e dividida entre as telas abertas.
// Os botões que aparecem juntos pedem a relação numa chamada só (até 100 por vez). Um pedido ou um aceite muda o
// estado na hora em todas as telas abertas, e nas outras abas da mesma conta pelo BroadcastChannel.
import { useSyncExternalStore } from 'react';
import type { Relacao } from './amigos.ts';
import { lerRelacoes } from './amigos.ts';
import type { Sessao } from './conta.ts';

const POR_VEZ = 100;
type Buscador = (s: Sessao, ids: string[]) => Promise<Record<string, Relacao>>;

let sessao: Sessao | null = null;
let buscador: Buscador = async (s, ids) => (await lerRelacoes(s, ids)).relacoes;
const cache = new Map<string, Relacao>();
const pedindo = new Set<string>();
const fila = new Set<string>();
const ouvintes = new Map<string, Set<() => void>>();
let agendado = false;
let canal: BroadcastChannel | null = null;

function avisar(u: string): void { for (const f of ouvintes.get(u) ?? []) f(); }
function avisarTodos(): void { for (const fs of ouvintes.values()) for (const f of fs) f(); }

function abrirCanal(): void {
  if (canal || typeof BroadcastChannel === 'undefined') return;
  try {
    canal = new BroadcastChannel('truco6-amizade');
    canal.onmessage = (ev: MessageEvent<{ conta: string; usuario: string; relacao: Relacao }>) => {
      if (ev.data?.conta !== sessao?.id) return;
      cache.set(ev.data.usuario, ev.data.relacao);
      avisar(ev.data.usuario);
    };
  } catch { canal = null; }
}

// A conta da aba. Trocar de conta (ou guardar a conta com Google) esquece as relações.
export function definirSessaoDasRelacoes(s: Sessao | null): void {
  if (s?.id !== sessao?.id || s?.anonimo !== sessao?.anonimo) {
    cache.clear(); pedindo.clear(); fila.clear();
    sessao = s;
    avisarTodos();
  } else sessao = s;
  if (s) abrirCanal();
}
export const sessaoDasRelacoes = (): Sessao | null => sessao;

// Muda a relação na hora (depois de um pedido, um aceite ou um bloqueio), em todas as telas e abas.
export function definirRelacao(usuario: string, relacao: Relacao): void {
  cache.set(usuario, relacao);
  avisar(usuario);
  if (sessao) canal?.postMessage({ conta: sessao.id, usuario, relacao });
}

// Esquece as relações guardadas: a próxima tela pergunta de novo à API.
export function esquecerRelacoes(): void { cache.clear(); avisarTodos(); }

async function buscarFila(): Promise<void> {
  agendado = false;
  const s = sessao;
  if (!s || !fila.size) return;
  const ids = [...fila];
  fila.clear();
  for (let i = 0; i < ids.length; i += POR_VEZ) {
    const parte = ids.slice(i, i + POR_VEZ);
    parte.forEach((u) => pedindo.add(u));
    try {
      const r = await buscador(s, parte);
      if (sessao !== s) return;
      for (const u of parte) if (r[u]) { cache.set(u, r[u]); avisar(u); }
    } catch { /* sem conexão: o botão não aparece; a próxima tela tenta de novo */ }
    finally { parte.forEach((u) => pedindo.delete(u)); }
  }
}

function pedir(u: string): void {
  if (!sessao || cache.has(u) || pedindo.has(u) || fila.has(u)) return;
  fila.add(u);
  if (!agendado) { agendado = true; queueMicrotask(() => { void buscarFila(); }); }
}

export function relacaoGuardada(u: string): Relacao | undefined { return cache.get(u); }

// O estado do botão para um jogador; undefined enquanto a API não respondeu.
export function useRelacao(usuario: string | null | undefined): Relacao | undefined {
  return useSyncExternalStore(
    (f) => {
      if (!usuario) return () => {};
      let fs = ouvintes.get(usuario);
      if (!fs) ouvintes.set(usuario, (fs = new Set()));
      fs.add(f);
      pedir(usuario);
      return () => { fs!.delete(f); };
    },
    () => (usuario ? cache.get(usuario) : undefined),
  );
}

// Só para os testes: troca a chamada à API e zera tudo.
export function _paraTestes(b: Buscador): void {
  buscador = b; cache.clear(); pedindo.clear(); fila.clear(); ouvintes.clear(); sessao = null; agendado = false;
}
export const _pedirParaTestes = pedir;
